// One logline / plot atom of a scene, written from OUTSIDE the document — the
// scene's draft rows on the sheet, the Navigator, the plan. The document's own
// save of the active logline / plot line is `useUpdateScene`; both keep the
// scenes list the editor reads in step with a write that takes seconds
// (#1841, 22.09 item 4):
//
//   · the list takes the words as the save LEAVES — a row that remounts
//     meanwhile seeds from the list, and would seed the old text;
//   · a list read already on the wire is cancelled — it was asked before these
//     words existed, and landing on top of them is the vanish;
//   · an atom the save created joins the list under the server's id, never as
//     a stand-in — the next save names its atom from the list, and a stand-in's
//     id is one no save can address.
//
// A refused write needs no rollback here: the settle invalidation re-reads the
// list either way, and the server still holds the old text.

import { request } from '@/api/apiUtils';
import { fountainScenesEntity } from '@/data/entities/fountainScenes';
import { getPrefixes, useCustomMutation, useOptimisticPrefixPatch } from '@/data/mutations';
import type { textAtomTypeId } from '@/enums';
import { useRequiredAppSelector } from '@/hooks';
import type { IFountainScene, TextAtoms } from '@/types';
import { textAtomEndpoint, textAtomsEndpoint } from './useTextAtomMutations';

export interface SceneTextSaveParams {
    sceneId: number;
    content: string;
    /** The atom to write; absent = the field has no atom yet, so one is created. */
    textAtomId?: number;
    /** Optional callback to sync editor content when the text changes from the
     *  navigator — the document's line under the heading shows the same atom. */
    onEditorSync?: (sceneId: number, text: string) => void;
}

type AtomResponse = { data?: { id?: number }; id?: number };

export function useSceneTextSave({ action, typeId }: { action: 'updateLogline' | 'updateSynopsis'; typeId: textAtomTypeId }) {
    const projectId = useRequiredAppSelector('projectId');
    // `{ projectId }` without a version matches every versioned copy of the list.
    const list = useOptimisticPrefixPatch<IFountainScene[]>(fountainScenesEntity.fetchers.list.key({ projectId }));
    const patchScene = (sceneId: number, update: (scene: IFountainScene) => IFountainScene) =>
        list.patch(old => old?.map(scene => (scene.id === sceneId ? update(scene) : scene)));

    return useCustomMutation<SceneTextSaveParams, AtomResponse>({
        mutationFn: async ({ sceneId, content, textAtomId }) => {
            if (textAtomId) {
                return request<AtomResponse>(textAtomEndpoint({ project_id: projectId, id: textAtomId }), {
                    method: 'PATCH',
                    body: JSON.stringify({ content }),
                });
            }
            return request<AtomResponse>(textAtomsEndpoint({ project_id: projectId }), {
                method: 'POST',
                body: JSON.stringify({
                    textable_model: 'scene',
                    textable_id: sceneId,
                    text_atom_type_id: typeId,
                    content,
                }),
            });
        },
        onMutate: async ({ sceneId, content, textAtomId }) => {
            await list.cancelInFlight();
            // The atom the request writes, by IDENTITY — never every atom of the
            // type, which would rewrite a draft too (#1841-09).
            patchScene(sceneId, scene => ({
                ...scene,
                text_atoms: scene.text_atoms?.map(atom =>
                    textAtomId !== undefined && atom.id === textAtomId ? { ...atom, content } : atom,
                ),
            }));
        },
        onSuccess: (response, { sceneId, content, onEditorSync }) => {
            const id = response?.data?.id ?? response?.id;
            if (id !== undefined) {
                patchScene(sceneId, scene => {
                    if (scene.text_atoms?.some(atom => atom.id === id)) return scene;
                    const created: TextAtoms = {
                        id,
                        text_atom_type_id: typeId,
                        content,
                        order: 0,
                        textable_id: sceneId,
                        textable_type: 'scene',
                        project_id: projectId,
                        tags: [],
                    };
                    return { ...scene, text_atoms: [...(scene.text_atoms ?? []), created] };
                });
            }
            onEditorSync?.(sceneId, content);
        },
        // [scenes, fountain.scenes] — prefix-covers every scene-keyed query
        invalidate: getPrefixes('scene', action),
    });
}
